import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ModalService } from '@app/shared/services/modal/modal.service';
import { MsalService } from '@azure/msal-angular';
import { ToastService } from '@app/shared/services/toast/toast.service';
import { SecurityService } from '@app/shared/services/security.service';
import { environment } from '@environments/environment';

/**
 * Per-endpoint custom error handling map.
 * Key   → URL substring that identifies the endpoint.
 * Value → set of HTTP status codes that endpoint handles itself.
 *
 * Only the listed status codes are suppressed for each endpoint.
 * Any other status code (e.g. 403 on validate-document) still goes
 * through the global interceptor behaviour.
 */
const CUSTOM_ERROR_HANDLING: ReadonlyMap<string, ReadonlySet<number>> = new Map([
  ['customers/',        new Set([409])],  // Whitelist validation   – own 409 handling
  ['validate-document', new Set([409])],  // Document validation    – own 409 handling
  ['/users',            new Set([403])],  // Create account         – own 403 modal (captcha)
]);

/**
 * Unified HTTP error handler interceptor
 *
 * Handles:
 * - 0:   Network error → redirects to /auth/error?reason=network_error
 * - 401: Session expired → shows modal (logoutRedirect or /auth/error?reason=session_expired)
 * - 403: Forbidden → redirects to /auth/error?reason=access_denied
 * - 404: Not found → skipped for custom-handled endpoints
 * - 409: Conflict → skipped for custom-handled endpoints
 * - 429: Rate limit → logged
 * - 5xx: Server error → logged
 *
 * @security OWASP A05 - Security Misconfiguration
 * @security OWASP A09 - Security Logging and Monitoring Failures
 * @security Never exposes technical details to users
 * @observability Logs errors with correlation IDs for traceability
 */
export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const modalService = inject(ModalService);
  const msalService = inject(MsalService);
  const toastService = inject(ToastService);
  const securityService = inject(SecurityService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const correlationId = req.headers.get('X-Correlation-Id') ?? crypto.randomUUID();

      // Secure logging - no PII, no sensitive data, no stack traces
      console.error('[HTTP Error]', {
        correlationId,
        status: error.status,
        statusText: error.statusText,
        url: sanitizeUrl(req.url),
        method: req.method,
        timestamp: new Date().toISOString()
      });

      handleHttpError(error, req.url, router, correlationId, modalService, msalService, toastService, securityService);

      // Return generic error to caller (NEVER expose backend details)
      return throwError(() => ({
        message: getGenericErrorMessage(error.status),
        status: error.status,
        correlationId,
        timestamp: new Date().toISOString()
      }));
    })
  );
};

/**
 * Handle specific HTTP error codes with appropriate routing.
 * @security Implements defense in depth by routing to safe error pages.
 */
function handleHttpError(
  error: HttpErrorResponse,
  requestUrl: string,
  router: Router,
  correlationId: string,
  modalService: ModalService,
  msalService: MsalService,
  toastService: ToastService,
  securityService: SecurityService,
): void {
  switch (error.status) {
    case 0:
      console.error('[Network Error] No connection to server', { correlationId });
      router.navigate(['/error/500'], {
        queryParams: { reason: 'network_error', correlationId }
      });
      break;

    case 401:
      console.warn('[Auth] Unauthorized access - token may be expired', { correlationId });
      handle401Unauthorized(correlationId, modalService, msalService, securityService, router);
      break;

    case 403:
      console.warn('[Auth] Access forbidden - insufficient permissions', { correlationId });
      if (!shouldHandleCustomError(requestUrl, 403)) {
        router.navigate(['/error/error-inicio-sesion'], {
          queryParams: { reason: 'access_denied', correlationId }
        });
      }
      break;

    case 404:
      console.warn('[HTTP] Resource not found', {
        correlationId,
        url: sanitizeUrl(requestUrl)
      });
      break;

    case 409:
      if (!shouldHandleCustomError(requestUrl, 409)) {
        console.warn('[HTTP] Conflict', { correlationId });
      }
      break;

    case 429:
      console.warn('[HTTP] Rate limit exceeded', { correlationId });
      break;

    case 500:
    case 502:
    case 503:
    case 504:
      console.error('[Server Error] Backend error', {
        correlationId,
        status: error.status,
        statusText: error.statusText
      });
      toastService.showError(getGenericErrorMessage(error.status));
      break;

    default:
      if (error.status >= 400) {
        console.error('[HTTP] Client/Server error', {
          correlationId,
          status: error.status
        });
      }
  }
}

/**
 * Shows a session-expired modal. On confirm → logoutRedirect; on cancel → /auth/error.
 */
function handle401Unauthorized(
  correlationId: string,
  modalService: ModalService,
  msalService: MsalService,
  securityService: SecurityService,
  router: Router
): void {
  const modalRef = modalService.confirm({
    title: 'Tu sesión ha caducado. Vuelve a iniciar sesión',
    image: 'assets/images/padlock_close.svg',
    confirmText: 'Iniciar Sesión',
    cancelText: 'Cerrar'
  });

  modalRef.afterClosed().subscribe((confirmed) => {
    if (confirmed) {
      securityService.logout().then(() => {
        const idTokenHint = msalService.instance.getActiveAccount()?.idToken;
        msalService.logoutRedirect({
          idTokenHint,
          postLogoutRedirectUri: environment.B2C_CONFIG.postLogoutRedirectUri
        });
      });
    } else {
      router.navigate(['/auth/error'], {
        queryParams: { reason: 'session_expired', correlationId }
      });
    }
  });
}

/**
 * Returns true when the given endpoint+status combination is handled
 * by the caller itself, so the interceptor must skip its generic behaviour.
 */
function shouldHandleCustomError(url: string, status: number): boolean {
  for (const [endpoint, statuses] of CUSTOM_ERROR_HANDLING) {
    if (url.includes(endpoint) && statuses.has(status)) {
      return true;
    }
  }
  return false;
}

/**
 * Returns a safe, generic error message based on status code.
 * @security Never exposes internal error details.
 */
function getGenericErrorMessage(status: number): string {
  const messages: Record<number, string> = {
    0:   'No se pudo conectar con el servidor. Verifica tu conexión a internet.',
    400: 'La solicitud contiene datos inválidos. Por favor, verifica e intenta nuevamente.',
    401: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
    403: 'No tienes permisos para realizar esta acción.',
    404: 'El recurso solicitado no fue encontrado.',
    409: 'Ha ocurrido un conflicto con el recurso solicitado.',
    429: 'Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente.',
    500: 'Error del servidor. Por favor, intenta nuevamente más tarde.',
    502: 'Error de conexión con el servidor. Por favor, intenta nuevamente.',
    503: 'El servicio no está disponible temporalmente. Por favor, intenta más tarde.',
    504: 'El servidor tardó demasiado en responder. Por favor, intenta nuevamente.'
  };

  return messages[status] ?? 'Ha ocurrido un error. Por favor, intenta nuevamente.';
}

/**
 * Sanitizes a URL for safe logging by stripping query params and hash.
 * @security Prevents accidental logging of tokens or PII in query strings.
 */
function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    return url.split('?')[0];
  }
}
